import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const RECENTLY_VIEWED_KEY = 'scanmarket_recently_viewed';
const MAX_RECENTLY_VIEWED = 12;

const BrowsingContext = createContext(null);

export function BrowsingProvider({ children }) {
  const [externalClickCount, setExternalClickCount] = useState(0);
  const [lastClickedSource, setLastClickedSource] = useState(null);
  const [recentlyViewed, setRecentlyViewed] = useState(() => {
    try {
      const stored = localStorage.getItem(RECENTLY_VIEWED_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  // Persist recently viewed to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify(recentlyViewed));
    } catch {}
  }, [recentlyViewed]);

  const trackExternalClick = useCallback((listing) => {
    setExternalClickCount(c => c + 1);
    setLastClickedSource(listing.source);

    // Add to recently viewed (dedupe by source+id)
    setRecentlyViewed(prev => {
      const key = `${listing.source}_${listing.source_listing_id}`;
      const filtered = prev.filter(l => `${l.source}_${l.source_listing_id}` !== key);
      const entry = {
        source: listing.source,
        source_listing_id: listing.source_listing_id,
        title: listing.title,
        price_chf: listing.price_chf,
        image_url: listing.image_url,
        listing_url: listing.listing_url,
        city: listing.city,
        deal_score: listing.deal_score,
        viewed_at: new Date().toISOString(),
      };
      return [entry, ...filtered].slice(0, MAX_RECENTLY_VIEWED);
    });
  }, []);

  const removeRecentlyViewed = useCallback((source, source_listing_id) => {
    setRecentlyViewed(prev =>
      prev.filter(l => !(l.source === source && l.source_listing_id === source_listing_id))
    );
  }, []);

  const clearAllRecentlyViewed = useCallback(() => {
    setRecentlyViewed([]);
  }, []);

  const resetClickCount = useCallback(() => {
    setExternalClickCount(0);
  }, []);

  return (
    <BrowsingContext.Provider value={{
      externalClickCount,
      lastClickedSource,
      recentlyViewed,
      trackExternalClick,
      resetClickCount,
      removeRecentlyViewed,
      clearAllRecentlyViewed,
    }}>
      {children}
    </BrowsingContext.Provider>
  );
}

export function useBrowsing() {
  return useContext(BrowsingContext);
}
